import { FieldArray, Formik, getIn } from "formik"
import i18next from "i18next";
import { Accordion, AccordionContext, Button, Card, Col, Form, Modal, Row, useAccordionButton } from 'react-bootstrap';
import SpinnerButton from "../SpinnerButton/SpinnerButton";
import _ from "lodash";
import { stringToOption } from "src/utils";
import ValidationError from "../ValidationError/ValidationError";
import { FaInfoCircle, FaSortUp } from "react-icons/fa";
import ToolTip from "../Tooltip/Tooltip";
import Select from 'react-select';
import React, { useContext, useEffect, useState } from "react";
import { useApiClient } from "src/hooks/useApiClient";
import { useVehicleMassiveUpdateContext } from "./VehicleMassiveUpdateContext";
import { useConfirmAction } from "src/hooks/useConfirmAction";
import { useErrorToast } from "src/hooks/useErrorToast";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Utils from './vehicleMassiveUpdateUtils';

type Props = {
  isOpen?: boolean;
  closeModal?: (reload?: boolean) => void;
  loading?: boolean;
}

const salableByOptionsMap = {
  MASERATI: 'MASERATI',
  VOLKSWAGEN: 'VOLKSWAGEN',
  STELLANTIS: 'STELLANTIS',
  // STELLANTIS_YOU: 'STELLANTIS&YOU',
  // FACILE: 'FACILE',
}

const salableByOptions: any[] = Object.entries(salableByOptionsMap).map(([value, label]) => ({ value, label }));

function ContextAwareToggle({ children, eventKey, callback = _.noop, className }: {
  children: React.ReactNode;
  eventKey: string;
  callback?: CallableFunction;
  className?: string;
}) {
  const { activeEventKey } = useContext(AccordionContext);
  const isCurrentEventKey = activeEventKey === eventKey;

  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => callback && callback(!isCurrentEventKey),
  );

  return (
    <div onClick={decoratedOnClick} className={"my-1 " + (className ?? '')} style={{ cursor: 'pointer' }}>
      {children}
    </div>
  );
}

export default function VehicleMassiveUpdateModal({ isOpen, closeModal = _.noop }: Props) {
  const { apiClient, handleApi } = useApiClient();
  const [dealerOptions, setDealerOptions] = useState<any[]>([]);
  const { ids, reset } = useVehicleMassiveUpdateContext();
  const { confirmAction, isConfirmShown } = useConfirmAction();
  const { showError } = useErrorToast();
  const navigate = useNavigate();
  const [isExtraPercRentalOpen, setExtraPercRentalOpen] = useState(false);

  useEffect(() => {
    handleApi(apiClient.dealerController.getRegisterDealers())
      .then((res) =>
        setDealerOptions(res.map((r) => stringToOption(r.ragione_sociale)))
      )
      .catch((err) => { });
  }, []);

  const onClose = () => {
    setExtraPercRentalOpen(false);
    closeModal?.()
  }

  const defaultValueByField = () => ({
    limited_visibility: [],
    data_disp: '',
    promo_note: null,
    extra_sconto_perc: null,
    prezzo_listino: null,
    visibility_tag: null,
    lista_noleggiatori_quotatore: Utils.listaNoleggiatori(),
  })

  const initialValues: any = {
    ...defaultValueByField(),
    limited_visibility_enabled: false,
    visibility_tag_enabled: false,
    data_disp_enabled: false,
    promo_note_enabled: false,
    data_disp_clear: false,
    promo_note_clear: false,
    extra_sconto_perc_enabled: false,
    prezzo_listino_enabled: false,
  };

  const handleOnSubmit = async (values, formikHelpers) => {
    if (isConfirmShown) return;

    try {
      const basePayload = Utils.buildBasePayload(values);
      const noleggiatoriPayload = Utils.buildNoleggiatoriPayload(values);

      let payload = Utils.sanitizePayload({ ...basePayload, ...noleggiatoriPayload });

      if (_.isEmpty(payload)) {
        toast.error(i18next.t('vehicle_massive_update_no_field_value'));
        return;
      }

      payload.ids = ids;
      console.debug({ payload });
      const confirmed = await confirmAction({
        title: i18next.t('confirm'),
        message: i18next.t('vehicle_massive_update_confirmation_message', { count: ids?.length }),
      });

      if (!confirmed) return;

      formikHelpers?.setSubmitting?.(true);
      await apiClient.vehicleController.putBatchVehicles(payload);
      toast.success(i18next.t('executed_successfully'));
      onClose()
      reset?.();
      setTimeout(() => navigate(0), 1000);
    } catch (error) {
      showError(error, i18next.t('error_massive_update'))
    } finally {
      formikHelpers?.setSubmitting?.(false)
    }
  };

  const handleValidate = (values) => {
    const errors: any = {};

    Utils.validateNumericField(values, errors, 'extra_sconto_perc', true);
    Utils.validateNumericField(values, errors, 'prezzo_listino');
    Utils.validateRequiredField(values, errors, 'data_disp');
    Utils.validateRequiredField(values, errors, 'promo_note');
    Utils.validateRequiredField(values, errors, 'limited_visibility');
    Utils.validateRequiredField(values, errors, 'visibility_tag');
    Utils.validateNoleggiatori(values, errors);

    return errors;
  };

  const handleEnabler = (e, setFieldValue, handleChange) => {
    const isChecked = e.target.checked
    const fieldName = e.target.name?.split('_enabled')?.[0];

    if (!isChecked) {
      const defaults = defaultValueByField();
      setFieldValue(fieldName, defaults[fieldName]);
      setFieldValue(`${fieldName}_clear`, false);

      if (fieldName == 'extra_sconto_perc') {
        setExtraPercRentalOpen(false);
        setFieldValue('lista_noleggiatori_quotatore', defaults['lista_noleggiatori_quotatore'])
      }
    }

    handleChange?.(e);
  }

  const renderClearCheck = (values, setFieldValue, fieldName: string) => (
    <Form.Check
      type="checkbox"
      className="mt-1"
      id={`${fieldName}_clear`}
      name={`${fieldName}_clear`}
      label={i18next.t('vehicle_massive_update_clear_field', { defaultValue: "Svuota campo" })}
      disabled={!values[`${fieldName}_enabled`]}
      checked={!!values[`${fieldName}_clear`]}
      onChange={(e) => {
        setFieldValue(fieldName, defaultValueByField()[fieldName]);
        setFieldValue(`${fieldName}_clear`, e.target.checked);
      }}
    />
  )

  const renderFieldError = (errors, fieldName: string) => {
    const error = getIn(errors, fieldName);

    if (error) {
      return (
        <small className="text-danger">
          {error}
        </small>
      )
    }

    return null;
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title className='display-7'>{i18next.t("vehicle_massive_update_title", { count: ids?.length })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fw-bold mb-4 px-4">
          {i18next.t("vehicle_massive_update_instructions")}
        </div>
        <Formik
          initialValues={initialValues}
          validate={handleValidate}
          onSubmit={handleOnSubmit}
        >
          {
            ({ errors, values, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
              <Form autoComplete='off' onSubmit={handleSubmit}>
                <Row>
                  <Col className="d-flex align-items-start px-4">
                    <Form.Check
                      type="checkbox"
                      label={''}
                      onChange={(e) => handleEnabler(e, setFieldValue, handleChange)}
                      name="limited_visibility_enabled"
                    />
                    <Form.Group className="mb-3 w-100">
                      <Form.Label>
                        {i18next.t('limited_visibility')}
                        <small id="info-die-required" className='text-muted ms-2'>
                          <FaInfoCircle size={18} className="mb-1" />
                          <ToolTip
                            anchorId="info-die-required"
                            content={i18next.t('limited_visibility_disclaimer')}
                            className="default"
                          />
                        </small>
                      </Form.Label>

                      <Select
                        menuPosition="fixed"
                        onChange={(opt) => {
                          setFieldValue('limited_visibility', opt || []);
                        }}
                        value={values.limited_visibility}
                        name="limited_visibility"
                        options={dealerOptions}
                        isMulti={true}
                        isDisabled={!values.limited_visibility_enabled}
                        className={
                          'hor-singleselect group-input ' +
                          (errors.limited_visibility && touched.limited_visibility
                            ? 'error-border'
                            : '')
                        }
                      />
                      <ValidationError {...{ errors, touched, name: 'limited_visibility' }} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col className="d-flex align-items-start px-4">
                    <Form.Check
                      type="checkbox"
                      label={''}
                      onChange={(e) => handleEnabler(e, setFieldValue, handleChange)}
                      name="visibility_tag_enabled"
                    />
                    <Form.Group className="mb-3 w-100">
                      <Form.Label>
                        {i18next.t('salable_by')}
                      </Form.Label>

                      <Select
                        menuPosition="fixed"
                        value={stringToOption(values?.visibility_tag)}
                        onChange={(opt) => {
                          setFieldValue('visibility_tag', opt?.value || null);
                        }}
                        name="visibility_tag"
                        options={salableByOptions}
                        isClearable={true}
                        isDisabled={!values.visibility_tag_enabled}
                        className={
                          'hor-singleselect group-input ' +
                          (errors.visibility_tag && touched.visibility_tag
                            ? 'error-border'
                            : '')
                        }
                      />
                      <ValidationError {...{ errors, touched, name: 'visibility_tag' }} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col className="d-flex align-items-start px-4">
                    <Form.Check
                      type="checkbox"
                      label={''}
                      onChange={(e) => handleEnabler(e, setFieldValue, handleChange)}
                      name="data_disp_enabled"
                    />
                    <Form.Group className="mb-3 w-100">
                      <Form.Label>{i18next.t('expected_availability_date')}</Form.Label>
                      <Form.Control
                        key={`${values.data_disp_enabled}-${values.data_disp_clear}`}
                        disabled={!values.data_disp_enabled || values.data_disp_clear}
                        onChange={handleChange}
                        name="data_disp"
                        type="date"
                      />
                      <ValidationError {...{ errors, touched, name: 'data_disp' }} />
                      {renderClearCheck(values, setFieldValue, 'data_disp')}
                    </Form.Group>
                  </Col>
                </Row >
                <Row>
                  <Col className="d-flex align-items-start px-4">
                    <Form.Check
                      type="checkbox"
                      label={''}
                      onChange={(e) => handleEnabler(e, setFieldValue, handleChange)}
                      name="promo_note_enabled"
                    />
                    <Form.Group className="mb-3 w-100">
                      <Form.Label>{i18next.t('promo_note')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        key={values.promo_note_enabled}
                        disabled={!values.promo_note_enabled || values.promo_note_clear}
                        onChange={handleChange}
                        value={values.promo_note}
                        name="promo_note"
                        type="text"
                      />
                      <ValidationError {...{ errors, touched, name: 'promo_note' }} />
                      {renderClearCheck(values, setFieldValue, 'promo_note')}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col className="d-flex align-items-start px-4">
                    <Form.Check
                      type="checkbox"
                      label={''}
                      onChange={(e) => handleEnabler(e, setFieldValue, handleChange)}
                      name="extra_sconto_perc_enabled"
                    />
                    <Form.Group className="mb-3 w-100">
                      <Form.Label>{i18next.t('extra_list_price_discount_percentage')}</Form.Label>
                      <Form.Control
                        key={values.extra_sconto_perc_enabled}
                        disabled={!values.extra_sconto_perc_enabled}
                        onChange={handleChange}
                        value={values.extra_sconto_perc}
                        name="extra_sconto_perc"
                        type="text"
                      />
                      <ValidationError {...{ errors, touched, name: 'extra_sconto_perc' }} />
                      {values.extra_sconto_perc_enabled && <Accordion>
                        {!isExtraPercRentalOpen && <ContextAwareToggle eventKey="0" callback={setExtraPercRentalOpen} className="text-decoration-underline">
                          {i18next.t('massive_update_set_value_by_rental')}
                        </ContextAwareToggle>}

                        <Accordion.Collapse eventKey="0">
                          <Row className="m-0 mt-2">
                            <Card style={{ backgroundColor: '#dddd', borderRadius: 8 }}>
                              <div className="d-flex align-items-center justify-content-between">
                                <h6 className="py-2 m-0">{i18next.t('massive_update_value_by_rental')}
                                </h6>
                                <ContextAwareToggle eventKey="0" className="d-flex align-items-center" callback={setExtraPercRentalOpen}>
                                  <div>{i18next.t('hide')}</div>
                                  <div><FaSortUp className="mt-2" /></div>
                                </ContextAwareToggle>
                              </div>

                              <div className="mb-2">
                                <small>{i18next.t('massive_update_extra_sconto_perc_override')}</small>
                              </div>

                              <FieldArray name="lista_noleggiatori_quotatore">
                                {() => (
                                  <div className="pt-2">
                                    {(values.lista_noleggiatori_quotatore || []).map((item: any, idx: number) => (
                                      <Row key={idx} className="mb-2">
                                        <Col md={6} className="fw-bold">
                                          {item.noleggiatore}
                                        </Col>
                                        <Col>
                                          <Form.Group>
                                            <Form.Control
                                              value={item.extra_sconto_perc ?? ''}
                                              type="text"
                                              onChange={(e) => {
                                                setFieldValue(`lista_noleggiatori_quotatore.${idx}.extra_sconto_perc`, e.target.value)
                                              }}
                                              name={`lista_noleggiatori_quotatore.${idx}.extra_sconto_perc`}
                                            />
                                            {renderFieldError(errors, `lista_noleggiatori_quotatore.${idx}.extra_sconto_perc`)}
                                          </Form.Group>
                                        </Col>

                                      </Row>
                                    ))}
                                  </div>
                                )}
                              </FieldArray>
                            </Card>
                          </Row>
                        </Accordion.Collapse>
                      </Accordion>
                      }
                    </Form.Group>
                  </Col>
                  <Row>
                    <Col className="d-flex align-items-start px-4">
                      <Form.Check
                        type="checkbox"
                        label={''}
                        onChange={(e) => handleEnabler(e, setFieldValue, handleChange)}
                        name="prezzo_listino_enabled"
                      />
                      <Form.Group className="mb-3 w-100">
                        <Form.Label>{i18next.t('list_price')}</Form.Label>
                        <Form.Control
                          key={values.prezzo_listino_enabled}
                          disabled={!values.prezzo_listino_enabled}
                          onChange={handleChange}
                          value={values.prezzo_listino}
                          name="prezzo_listino"
                          type="text"
                        />
                        <ValidationError {...{ errors, touched, name: 'prezzo_listino' }} />
                      </Form.Group>
                    </Col>
                  </Row>
                </Row>
                <Row className="mt-4">
                  <Col md={12} className="d-flex justify-content-end">
                    <Button className="me-2" disabled={isSubmitting} onClick={onClose} variant='link'>
                      {i18next.t('cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                    >
                      <SpinnerButton
                        busy={isSubmitting}
                        label={i18next.t('apply_changes')}
                      />
                    </Button>
                  </Col>
                </Row>
              </Form >
            )}
        </Formik>
      </Modal.Body>
    </Modal>

  )
}
